import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import reportCardApi, { errorMessage } from "./reportCardApi";
import ClassMultiSelect from "./ClassMultiSelect";

export const AUTO_CALCS = [
  { value: "ATTENDANCE", label: "Attendance (present / total)", input: "FRACTION" },
  { value: "PROMOTED_CLASS", label: "Promoted to Class", input: "CLASS" },
  { value: "RESULT_STATUS", label: "Result (Pass / Fail / Compartment)", input: "TEXT" },
  { value: "PERCENTAGE", label: "Overall Percentage", input: "NUMBER" },
  { value: "OVERALL_GRADE", label: "Overall Grade", input: "TEXT" },
  { value: "RANK", label: "Rank in Section", input: "TEXT" },
];
const INPUT_TYPES = ["TEXT", "TEXTAREA", "NUMBER", "DATE", "DROPDOWN", "CLASS", "FRACTION"];

const EMPTY = {
  field_key: "", title: "", value_source: "MANUAL", auto_calc_key: "", input_type: "TEXT",
  options: [], default_value: "", is_required: false, allow_override: false,
  scope: "SESSION", report_sort_order: 0, status: "active", class_ids: [],
};

const toKey = (title) => title.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, "");

/** Settings -> Report Footer fields (Attendance, Promoted to Class, Remarks ...) */
const FooterFieldTab = ({ canEdit }) => {
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classMap, setClassMap] = useState({});
  const [edit, setEdit] = useState(null);
  const [optionsText, setOptionsText] = useState("");

  const load = async () => {
    try {
      const list = await reportCardApi.footerFields.list();
      setRows(list);
      const map = {};
      await Promise.all(list.map(async (r) => { map[r.id] = await reportCardApi.footerFields.getClasses(r.id); }));
      setClassMap(map);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  useEffect(() => {
    load();
    reportCardApi.activeClasses().then(setClasses).catch(() => setClasses([]));
  }, []);

  const open = (row) => {
    const r = row ? { ...EMPTY, ...row, auto_calc_key: row.auto_calc_key || "", class_ids: classMap[row.id] || [] } : { ...EMPTY, report_sort_order: rows.length + 1 };
    setOptionsText(Array.isArray(r.options) ? r.options.join(", ") : "");
    setEdit(r);
  };

  const set = (patch) => setEdit((e) => ({ ...e, ...patch }));

  const onTitle = (title) => set({ title, ...(edit.id ? {} : { field_key: toKey(title) }) });

  const onSource = (value_source) =>
    set(value_source === "MANUAL"
      ? { value_source, auto_calc_key: "", allow_override: false }
      : { value_source, auto_calc_key: AUTO_CALCS[0].value, input_type: AUTO_CALCS[0].input });

  const onCalc = (auto_calc_key) =>
    set({ auto_calc_key, input_type: AUTO_CALCS.find((a) => a.value === auto_calc_key)?.input || "TEXT" });

  const save = async () => {
    if (!edit.title?.trim()) return toast.error("Title is required");
    if (!edit.field_key?.trim()) return toast.error("Key is required");
    const options = edit.input_type === "DROPDOWN" ? optionsText.split(",").map((x) => x.trim()).filter(Boolean) : null;
    if (edit.input_type === "DROPDOWN" && !options.length) return toast.error("Enter dropdown options, separated by commas");
    const body = {
      field_key: edit.field_key,
      title: edit.title.trim(),
      value_source: edit.value_source,
      auto_calc_key: edit.value_source === "AUTO" ? edit.auto_calc_key : null,
      input_type: edit.input_type,
      options,
      default_value: edit.default_value || null,
      is_required: !!edit.is_required,
      allow_override: edit.value_source === "AUTO" ? !!edit.allow_override : false,
      scope: edit.scope,
      report_sort_order: Number(edit.report_sort_order) || 0,
      status: edit.status,
      class_ids: edit.class_ids,
    };
    try {
      if (edit.id) await reportCardApi.footerFields.update(edit.id, body);
      else await reportCardApi.footerFields.create(body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? All values entered for it will also be deleted.`)) return;
    try {
      await reportCardApi.footerFields.remove(row.id);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const className = (id) => classes.find((c) => c.id === id)?.class_name || "";

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">
          Rows printed below the marks. <b>Auto</b> fields are calculated by the system; <b>Manual</b> fields are typed in by teachers.
        </p>
        {canEdit && <Button size="sm" onClick={() => open(null)}>New Footer Field</Button>}
      </div>

      <Table striped bordered size="sm">
        <thead>
          <tr><th style={{ width: 70 }}>Order</th><th>Title</th><th>Value</th><th>Per</th><th>Classes</th><th style={{ width: 90 }}>Status</th>{canEdit && <th style={{ width: 110 }}>Action</th>}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.report_sort_order}</td>
              <td>{r.title}<br /><small className="text-muted">{r.field_key}</small></td>
              <td>
                {r.value_source === "AUTO"
                  ? <>Auto: {AUTO_CALCS.find((a) => a.value === r.auto_calc_key)?.label || r.auto_calc_key}{r.allow_override && <small className="text-muted"> (editable)</small>}</>
                  : <>Manual ({r.input_type.toLowerCase()})</>}
              </td>
              <td>{r.scope === "SESSION" ? "Year" : "Term"}</td>
              <td><small>{(classMap[r.id] || []).length ? classMap[r.id].map(className).join(", ") : <i className="text-muted">All</i>}</small></td>
              <td><span className={`badge bg-${r.status === "active" ? "success" : "secondary"}`}>{r.status}</span></td>
              {canEdit && (
                <td>
                  <Button size="sm" className="edit-button me-2" onClick={() => open(r)}><i className="fa-regular fa-pen-to-square"></i></Button>
                  <Button size="sm" variant="danger" onClick={() => remove(r)}><i className="fa fa-trash"></i></Button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted">No footer fields yet</td></tr>}
        </tbody>
      </Table>

      <Modal show={!!edit} onHide={() => setEdit(null)} centered backdrop="static" size="lg">
        <Modal.Header closeButton><Modal.Title>{edit?.id ? "Edit" : "New"} Footer Field</Modal.Title></Modal.Header>
        <Modal.Body>
          {edit && (
            <Row className="g-3">
              <Col lg={8}>
                <Form.Label className="form-view-label">Title (printed on report)</Form.Label>
                <Form.Control value={edit.title} placeholder="Class Teacher's Remarks" onChange={(e) => onTitle(e.target.value)} />
              </Col>
              <Col lg={4}>
                <Form.Label className="form-view-label">Key</Form.Label>
                <Form.Control value={edit.field_key} disabled={!!edit.id} onChange={(e) => set({ field_key: toKey(e.target.value) })} />
              </Col>

              <Col lg={4}>
                <Form.Label className="form-view-label">Value</Form.Label>
                <Form.Select value={edit.value_source} onChange={(e) => onSource(e.target.value)}>
                  <option value="MANUAL">Manual (teacher enters)</option>
                  <option value="AUTO">Auto (system calculates)</option>
                </Form.Select>
              </Col>
              {edit.value_source === "AUTO" ? (
                <>
                  <Col lg={5}>
                    <Form.Label className="form-view-label">Calculate</Form.Label>
                    <Form.Select value={edit.auto_calc_key} onChange={(e) => onCalc(e.target.value)}>
                      {AUTO_CALCS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </Form.Select>
                  </Col>
                  <Col lg={3} className="d-flex align-items-end">
                    <Form.Check id="ff-override" label="Teacher can change" checked={!!edit.allow_override} onChange={(e) => set({ allow_override: e.target.checked })} />
                  </Col>
                </>
              ) : (
                <>
                  <Col lg={4}>
                    <Form.Label className="form-view-label">Input Type</Form.Label>
                    <Form.Select value={edit.input_type} onChange={(e) => set({ input_type: e.target.value })}>
                      {INPUT_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</option>)}
                    </Form.Select>
                  </Col>
                  <Col lg={4} className="d-flex align-items-end">
                    <Form.Check id="ff-required" label="Required" checked={!!edit.is_required} onChange={(e) => set({ is_required: e.target.checked })} />
                  </Col>
                  {edit.input_type === "DROPDOWN" && (
                    <Col lg={12}>
                      <Form.Label className="form-view-label">Options (comma separated)</Form.Label>
                      <Form.Control value={optionsText} placeholder="Excellent, Very Good, Good, Needs Improvement" onChange={(e) => setOptionsText(e.target.value)} />
                    </Col>
                  )}
                  <Col lg={12}>
                    <Form.Label className="form-view-label">Default Value</Form.Label>
                    <Form.Control value={edit.default_value || ""} onChange={(e) => set({ default_value: e.target.value })} />
                  </Col>
                </>
              )}

              <Col lg={4}>
                <Form.Label className="form-view-label">One value per</Form.Label>
                <Form.Select value={edit.scope} onChange={(e) => set({ scope: e.target.value })}>
                  <option value="SESSION">Session (whole year)</option>
                  <option value="EXAM_GROUP">Exam Group (each term)</option>
                </Form.Select>
              </Col>
              <Col lg={4}>
                <Form.Label className="form-view-label">Row Order</Form.Label>
                <Form.Control type="number" min={0} value={edit.report_sort_order} onChange={(e) => set({ report_sort_order: e.target.value })} />
              </Col>
              <Col lg={4}>
                <Form.Label className="form-view-label">Status</Form.Label>
                <Form.Select value={edit.status} onChange={(e) => set({ status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col lg={12}>
                <Form.Label className="form-view-label">Classes</Form.Label>
                <ClassMultiSelect classes={classes} value={edit.class_ids} onChange={(ids) => set({ class_ids: ids })} />
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEdit(null)}>Close</Button>
          <Button onClick={save}>Save</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FooterFieldTab;
